import type { DataGridColumnProps } from '../../components/DataGrid/DataGrid';
import type { DataGridGetRowLinkOptionsFn } from '../../components/DataGrid/DroppableCell/DroppableCell';
import type { DataGridHeaderFilter } from '../../components/DataGrid/HeaderFilter/HeaderFilter';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';

import { exportUsersCsv } from '../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import {
  getStripeURL,
  getTestProps,
  prepareColumns,
  setInitialGridOptions,
} from '../../lib/helpers';

import CustomizableDataGrid from '../../components/CustomizableDataGrid/CustomizableDataGrid';
import { useGridFilters } from '../../components/DataGrid/useGridFilters';
import DataGridControl from '../../components/DataGridControl/DataGridControl';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import StatusBar from '../../components/StatusBar/StatusBar';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionButton from '../../components/Topbar/buttons/base/TopbarActionButton';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import { useAllUsers, useConstraints, useUsers } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { AvatarIcon, WarningIcon } from '../../images/shapes';

import UsersCustomExportModalForm from '../../form/UsersCustomExportModalForm/UsersCustomExportModalForm';
//:: Layout
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import { getDefinedColumns } from './definedColumns';

const getApiFilters = (filters: DataGridHeaderFilter) => {
  if (!filters) return {};

  const newFilters: Record<string, string | string[]> = {};

  Object.keys(filters).forEach((key) => {
    if (key === 'organization.name') {
      newFilters.organizationName = filters['organization.name'].filter;
    } else if (key === 'organization.id') {
      newFilters.organizationId = filters['organization.id'].filter;
    } else if (key == 'space.id') {
      newFilters.spaceId = filters['space.id'].filter;
    } else if (key === 'roles') {
      newFilters.roles = Array.isArray(filters[key].filter)
        ? filters[key].filter.join(',')
        : filters[key].filter;
    } else {
      newFilters[key] = filters[key].filter;
    }
  });

  return newFilters;
};

const testId = 'users';

interface UsersProps {
  /**
   * If page is for all users data
   */
  isAllUsers?: boolean;
}

const Users = ({ isAllUsers = false }: UsersProps) => {
  const modal = useModals();
  const [, setImpersonate] = useSessionStorageState('cms.impersonate');
  const [searchParams] = useSearchParams();

  const SORT_KEY = isAllUsers ? 'cms.all-users-sort' : 'cms.user-sort';
  const OPTIONS_KEY = isAllUsers
    ? 'cms.all-users-grid-state'
    : 'cms.user-grid-state';

  const usersGridContainer = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isRoleAdmin, isAdmin, userStorage: user } = use(UserContext);

  const usersLimit = useMemo(
    () => user?.data?.organizationLimits?.userLimit,
    [user],
  );

  const { entity: membersCount } = useConstraints('organization-users');

  const disabledAdding = useMemo(
    () =>
      isAllUsers || usersLimit === -1
        ? false
        : membersCount?.data && usersLimit && +membersCount.data >= usersLimit,
    [isAllUsers, usersLimit, membersCount],
  );

  const [firstLoading, setFirstLoading] = useState(true);
  const [initDataHasContent, setInitDataHasContent] = useState(false);
  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);
  const [editGrid, setEditGrid] = useState(false);
  const [filterGrid, setFilterGrid] = useState(!!searchParams.get('filters'));

  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(SORT_KEY);
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(OPTIONS_KEY);

  const {
    filters,
    filtersApplied,
    handleFiltersChange,
    page,
    handlePageChange,
    limit,
    handleLimitChange,
    removeAllFilters,
  } = useGridFilters(isAllUsers ? 'all-users' : 'users');

  const usersParams = useMemo(
    () => ({
      page,
      limit,
      ...getApiFilters(filters),
      ...(sort?.sortOrder ? { order_direction: sort?.sortOrder } : {}),
      ...(sort?.sortField ? { order_by: sort?.sortField } : {}),
    }),
    [page, limit, filters, sort?.sortOrder, sort?.sortField],
  );

  const usersOptions = useMemo(
    () => ({
      pause: isAllUsers,
    }),
    [isAllUsers],
  );

  const {
    data: users,
    errors: usersError,
    status: usersStatus,
    isLoading: usersAreLoading,
    pagination: usersPagination,
    reload: usersReload,
  } = useUsers(usersParams, usersOptions);

  const allUsersOptions = useMemo(
    () => ({
      pause: !isRoleAdmin || !isAllUsers,
    }),
    [isRoleAdmin, isAllUsers],
  );

  const {
    data: allUsers,
    errors: allUsersError,
    status: allUsersStatus,
    isLoading: allUsersAreLoading,
    pagination: allUsersPagination,
    reload: allUsersReload,
  } = useAllUsers(usersParams, allUsersOptions);

  useApiErrorsToast(usersError);
  useApiErrorsToast(allUsersError);

  const pagination = useMemo(
    () => (isAllUsers ? allUsersPagination : usersPagination),
    [allUsersPagination, isAllUsers, usersPagination],
  );

  useEffect(() => {
    if (pagination?.total_pages < page)
      handlePageChange(pagination.total_pages);
  }, [handlePageChange, page, pagination?.total_pages]);

  useEffect(() => {
    if (firstLoading) {
      setInitDataHasContent(false);
    }
    if (
      (!usersAreLoading && !isAllUsers) ||
      (!allUsersAreLoading && isAllUsers)
    )
      setFirstLoading(false);
  }, [allUsersAreLoading, firstLoading, isAllUsers, usersAreLoading]);

  useEffect(() => {
    if (!initDataHasContent && (users.length || allUsers.length)) {
      setInitDataHasContent(true);
    }
  }, [users.length, initDataHasContent, allUsers.length]);

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    removeAllFilters();
    removeSort();
    removeGridOptions();
    setGridOptions(undefined);
  }, [
    setSort,
    removeAllFilters,
    removeSort,
    removeGridOptions,
    setGridOptions,
  ]);

  const showGrid = useMemo(() => {
    if (!isAllUsers)
      return (
        users.length > 0 ||
        (usersAreLoading && !firstLoading) ||
        filtersApplied ||
        initDataHasContent
      );
    return (
      isRoleAdmin &&
      (allUsers.length > 0 ||
        (allUsersAreLoading && !firstLoading) ||
        filtersApplied ||
        initDataHasContent)
    );
  }, [
    isAllUsers,
    users.length,
    usersAreLoading,
    firstLoading,
    filtersApplied,
    initDataHasContent,
    isRoleAdmin,
    allUsers.length,
    allUsersAreLoading,
  ]);

  const emptyData = useMemo(() => {
    if (showGrid) return null;
    if (firstLoading && (!isAllUsers || isRoleAdmin)) {
      return (
        <Loader
          size="big"
          type="spinner-grid"
          {...getTestProps(testId, 'loading', 'testId')}
        />
      );
    }
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          {(usersStatus || allUsersStatus) === 403 ||
          (!isRoleAdmin && isAllUsers)
            ? t('Global.NoAccess')
            : t('Media.OnErrorMessage')}
        </div>
      </Heading>
    );
  }, [
    showGrid,
    firstLoading,
    isAllUsers,
    isRoleAdmin,
    usersStatus,
    allUsersStatus,
    t,
  ]);

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[], minWidth: number) => {
      setInitialGridOptions(
        cols,
        minWidth,
        usersGridContainer.current?.offsetWidth || 0,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    prepareColumns(
      gridOptions,
      editGrid,
      t,
      testId,
      setGridOptions,
      handleInitialGridOptions,
      setColumns,
      getDefinedColumns,
      isAllUsers,
      undefined,
      isRoleAdmin,
    );
  }, [
    editGrid,
    gridOptions,
    handleInitialGridOptions,
    isAllUsers,
    isRoleAdmin,
    setGridOptions,
    t,
  ]);

  const impersonateColumn = useMemo(
    () => ({
      accessor: '_impersonate',
      label: '',
      width: 50,
      minWidth: 50,
      flexGrow: 0,
      justify: 'center',
      notDraggable: true,
      render: () => (
        <AvatarIcon
          title={t('Global.Impersonate')}
          className="text-slate-500 w-4 min-w-4"
        />
      ),
      resizable: false,
      sortable: false,
    }),
    [t],
  );

  const allColumns = useMemo(
    () => [...(isAllUsers ? [impersonateColumn] : []), ...columns],
    [isAllUsers, impersonateColumn, columns],
  );

  const [isExporting, setIsExporting] = useState('');

  const onExportClick = async (key: string, filters?: Record<string, any>) => {
    setIsExporting(key);
    await exportUsersCsv(filters, key);
    setIsExporting('');
  };

  const exportActions = useMemo(
    () => [
      {
        key: 'paying-users',
        label: t('Users.ExportPaying'),
        onClick: () =>
          onExportClick('paying-users', {
            filterBySpacesPlan: 'minOnePaid',
          }),
      },
      {
        key: 'free-logged-users',
        label: t('Users.ExportFreeLogged'),
        onClick: () =>
          onExportClick('free-logged-users', {
            filterBySpacesPlan: 'minOneFree',
            loginFromDays: 0,
            loginToDays: 30,
          }),
      },
      {
        key: '90-days-users',
        label: t('Users.Export90Days'),
        onClick: () =>
          onExportClick('90-days-users', {
            loginFromDays: 90,
            loginToDays: 179,
          }),
      },
      {
        key: '180-days-users',
        label: t('Users.Export180Days'),
        onClick: () =>
          onExportClick('180-days-users', {
            loginFromDays: 180,
            loginToDays: 364,
          }),
      },
      {
        key: '365-days-users',
        label: t('Users.Export365Days'),
        onClick: () =>
          onExportClick('365-days-users', {
            loginFromDays: 365,
          }),
      },
      {
        key: 'custom',
        label: t('Users.ExportCustom'),
        onClick: () => {
          modal({
            title: t('Users.ExportCustom'),
            content: (
              <UsersCustomExportModalForm
                tableFilters={getApiFilters(filters)}
                onSubmit={(values) => exportUsersCsv(values, 'custom-users')}
                {...getTestProps(testId, 'export-custom', 'testId')}
              />
            ),
            size: 'lg',
          });
        },
      },
    ],
    [filters, modal, t],
  );

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (accessor, rowData) => {
      if (accessor === '_impersonate')
        return {
          to: '/',
          onClick: () => {
            setImpersonate({
              email: rowData.email,
              id: rowData.id,
            });
            FlotiqPlugins.unregisterAllPlugins();
          },
        };
      else if (accessor === 'paymentClientId') {
        return {
          to: getStripeURL(`/customers/${rowData.paymentClientId}`),
          target: '_blank',
          rel: 'noreferrer',
        };
      }
      return { to: `/users/edit/${rowData.id}` };
    },
    [setImpersonate],
  );

  return (
    <PageLayout
      page={isAllUsers ? 'admin/all-users' : 'users'}
      id={isAllUsers ? 'allUsers' : 'users'}
      menuItemOpen={isAllUsers ? 'admin' : ''}
      title={isAllUsers ? t('Global.AllUsers') : t('Global.Users')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <>
          {(isAdmin || isRoleAdmin) && (
            <TopbarButton
              label={t('Users.Add')}
              link={'/users/add'}
              {...(disabledAdding
                ? {
                    disabled: true,
                    tooltip: t('Users.LimitReached'),
                    tooltipPlacement: 'leftBottom',
                    additionalTooltipClasses: 'hidden md:block',
                  }
                : {})}
            />
          )}
          {isRoleAdmin && isAllUsers && (
            <>
              <TopbarButton
                label={t('Users.Export')}
                onClick={() => onExportClick('users')}
                disabled={isExporting === 'users'}
              />
              <TopbarActionMenu>
                {exportActions.map(({ key, label, onClick }) => (
                  <TopbarActionButton
                    key={key}
                    label={label}
                    onClick={onClick}
                    disabled={isExporting === key}
                  />
                ))}
              </TopbarActionMenu>
            </>
          )}
        </>
      }
      secondaryTopbar={
        <DataGridControl
          displayGridCheckboxes={showGrid && columns.length > 0}
          displayGridControl={showGrid}
          editGrid={editGrid}
          filterGrid={filterGrid}
          setEditGrid={setEditGrid}
          setFilterGrid={setFilterGrid}
          handleResetGrid={handleResetGrid}
          limit={limit}
          handleLimitChange={handleLimitChange}
          columns={columns}
          gridOptions={gridOptions}
          optionsLocalStorageKey={OPTIONS_KEY}
          setGridOptions={setGridOptions}
          columnsVisibilityAdditionalClasses={twMerge(
            isAllUsers && '-top-2 xs:top-[0.2rem]',
          )}
          testId={testId}
        />
      }
      noPaddings
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withDataGrid}>
        <div
          className={predefinedLayoutClasses.dataGridContainer}
          ref={usersGridContainer}
        >
          {showGrid ? (
            <CustomizableDataGrid
              columns={allColumns}
              setColumns={setColumns}
              data={isAllUsers ? allUsers : users}
              isLoading={isAllUsers ? allUsersAreLoading : usersAreLoading}
              sort={sort?.sortField || undefined}
              sortOrder={sort?.sortOrder || undefined}
              sortingLocalStorageKey={SORT_KEY}
              optionsLocalStorageKey={OPTIONS_KEY}
              setCurrentPage={handlePageChange}
              editGrid={editGrid}
              statusBar={
                <StatusBar
                  rows={pagination.count}
                  currentPage={page}
                  pagesCount={pagination.total_pages}
                  handlePageChange={handlePageChange}
                  handleDataUpdate={isAllUsers ? allUsersReload : usersReload}
                  resultsFrom={(pagination.current_page - 1) * limit + 1}
                  resultsTo={
                    (pagination.current_page - 1) * limit + pagination.count
                  }
                  resultsTotalCount={pagination.total_count}
                  {...getTestProps(testId, 'statusbar', 'testId')}
                />
              }
              noDataInfoText={t('Users.FiltersEmptyResult')}
              hasFilters={filterGrid}
              filters={filters}
              disableFilters={editGrid}
              onFilter={handleFiltersChange}
              getRowLinkOptions={getRowLinkOptions}
              additionalClasses="bg-white dark:bg-slate-900"
              {...getTestProps(testId, 'grid', 'testId')}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              {emptyData}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Users;
